export interface OrderPricingOption {
    title?: string;
    totalSteps: number;
    price: number;
    originalPrice?: number;
    discountPercent?: number;
    discountedPrice?: number;
    bonusSteps?: number;
    bonusTickets?: number;
    totalExecutionSteps?: number;
    totalTickets?: number;
}

export const getPricingBonusLabel = (item: OrderPricingOption): string =>
    (item.bonusSteps ?? 0) > 0
        ? ` · Tặng ${(item.bonusTickets ?? (item.bonusSteps ?? 0) * 38).toLocaleString('vi-VN')} vé`
        : '';

export interface OrderBonusTier {
    minSteps: number;
    maxSteps: number;
    bonusSteps: number;
}

export const getOrderBonusSteps = (steps: number, tiers: OrderBonusTier[]) =>
    tiers.find((tier) => steps >= tier.minSteps && steps <= tier.maxSteps)?.bonusSteps ?? 0;

export interface OrderDiscountTier {
    title?: string;
    minTotalDeposited: number;
    discountPercent: number;
}

export const getActiveDiscountTierTitle = (
    totalDeposited: number,
    tiers: OrderDiscountTier[],
): string | undefined => tiers
    .filter((tier) => tier.minTotalDeposited <= totalDeposited && tier.discountPercent > 0)
    .sort((first, second) => second.minTotalDeposited - first.minTotalDeposited)[0]?.title;

export interface ParsedOrderInput {
    code: string;
    totalSteps: number;
    hasValidFormat: boolean;
}

export const getOrderInputLimitError = (orders: ParsedOrderInput[], tiers: OrderBonusTier[]): string | undefined => {
    const totalsByCode = new Map<string, { steps: number; bonusSteps: number }>();
    for (const order of orders) {
        const current = totalsByCode.get(order.code) ?? { steps: 0, bonusSteps: 0 };
        const bonusSteps = getOrderBonusSteps(order.totalSteps, tiers);
        const total = { steps: current.steps + order.totalSteps + bonusSteps, bonusSteps: current.bonusSteps + bonusSteps };
        totalsByCode.set(order.code, total);
        if (total.steps > 1000) {
            return `Mã ${order.code} cần tổng ${total.steps.toLocaleString('vi-VN')} lượt giúp, đã gồm ${(total.bonusSteps * 38).toLocaleString('vi-VN')} vé tặng; vượt giới hạn 1.000 lượt/mã/ngày. Vui lòng giảm số lượt mua.`;
        }
    }
    return undefined;
};

export interface OrderPriceEstimate {
    orderCount: number;
    originalTotal: number;
    payableTotal: number;
}

export const parseBulkOrderInput = (orderCode: string, defaultSteps: number): ParsedOrderInput[] => {
    const lines = orderCode
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean);

    return lines.map((line) => {
        const parts = line.split('|').map((part) => part.trim());
        const rawCode = parts[0] || '';
        const invitedCode = rawCode.match(/--(.*?)--/);
        const code = (invitedCode?.[1] || rawCode).trim();
        const totalSteps = parts.length === 1
            ? (lines.length === 1 ? defaultSteps : 1)
            : Number(parts[1]);

        return {
            code,
            totalSteps,
            hasValidFormat: parts.length <= 2
                && /^[A-Za-z0-9]{10}$/.test(code)
                && Number.isInteger(totalSteps)
                && totalSteps > 0,
        };
    });
};

export const getCouponValidationSteps = (orderCode: string, defaultSteps: number): number => {
    const orders = parseBulkOrderInput(orderCode, defaultSteps)
        .filter((order) => order.hasValidFormat && order.code.length === 10);
    return orders.length > 0
        ? Math.min(...orders.map((order) => order.totalSteps))
        : defaultSteps;
};

export const estimateBulkOrderPrice = (
    orderCode: string,
    defaultSteps: number,
    pricing: OrderPricingOption[],
    coupon?: { discountPercent: number; maxSteps: number } | null,
): OrderPriceEstimate | undefined => {
    const orders = parseBulkOrderInput(orderCode, defaultSteps);
    if (
        orders.length === 0
        || orders.some((order) => !order.hasValidFormat || order.code.length !== 10)
        || pricing.length === 0
    ) {
        return undefined;
    }

    const singleStepPricing = pricing.find((item) => item.totalSteps === 1);
    let originalTotal = 0;
    let payableTotal = 0;
    let couponApplied = false;

    for (const order of orders) {
        const packagePricing = pricing
            .filter((item) => item.totalSteps > 1 && item.totalSteps <= order.totalSteps)
            .sort((first, second) => second.totalSteps - first.totalSteps)[0];
        const exactPricing = pricing.find((item) => item.totalSteps === order.totalSteps);
        const priceOption = packagePricing || exactPricing || singleStepPricing;
        if (!priceOption) return undefined;

        const usesPackagePrice = Boolean(packagePricing || exactPricing);
        const originalPrice = usesPackagePrice
            ? Math.round(((priceOption.originalPrice ?? priceOption.price) / priceOption.totalSteps) * order.totalSteps)
            : (priceOption.originalPrice ?? priceOption.price) * order.totalSteps;
        const vipDiscountedPrice = usesPackagePrice
            ? Math.round(((priceOption.discountedPrice ?? priceOption.price) / priceOption.totalSteps) * order.totalSteps)
            : Math.max(0, originalPrice - Math.round((originalPrice * (priceOption.discountPercent ?? 0)) / 100));
        const canApplyCoupon = coupon && !couponApplied && order.totalSteps <= coupon.maxSteps;
        const payablePrice = canApplyCoupon
            ? Math.max(0, vipDiscountedPrice - Math.round((vipDiscountedPrice * coupon.discountPercent) / 100))
            : vipDiscountedPrice;
        if (canApplyCoupon) couponApplied = true;

        originalTotal += originalPrice;
        payableTotal += payablePrice;
    }

    return { orderCount: orders.length, originalTotal, payableTotal };
};
